/**
 * The icons an account can be given, and the types it can be, for the account editor.
 *
 * ACCOUNT_ICONS is the same list as Account_Service::ICONS, which refuses anything else; a php test
 * (AccountIconListTest) reads this file to hold the two together. Every name is in the icon font the
 * page bundles, material-design-icons-iconfont 5, which is older than `savings` or `payments`.
 */
export const ACCOUNT_ICONS = [
	'account_balance',
	'account_balance_wallet',
	'local_atm',
	'attach_money',
	'monetization_on',
	'credit_card',
	'home',
	'directions_car',
	'school',
	'trending_up',
	'show_chart',
	'business_center',
];

/**
 * The stored types, which are Plaid's (Manual_Account_Creator::TYPES), with the words a person
 * reads. `brokerage` is kept as stored and called an investment.
 */
export const ACCOUNT_TYPES = [
	{ value: 'depository', label: 'Bank account' },
	{ value: 'credit', label: 'Credit card' },
	{ value: 'loan', label: 'Loan' },
	{ value: 'brokerage', label: 'Investment' },
];

const DEFAULT_ICON_BY_TYPE = {
	depository: 'account_balance',
	credit: 'credit_card',
	loan: 'home',
	brokerage: 'trending_up',
};

/**
 * A loan is most often a mortgage, but not always: a car loan drawn as a house reads as a second
 * mortgage. The name is all there is to go on, and it is usually enough ("Blazer EV Loan",
 * "IND 2022 CHEVROLET BOLT EUV").
 */
const CAR_LOAN_NAME = /\b(car|auto|vehicle|truck|suv|ev|chevrolet|chevy|ford|toyota|honda|nissan|tesla|hyundai|kia|subaru|mazda|jeep|gmc|dodge|volkswagen|vw|bmw|audi|lexus)\b/i;
const STUDENT_LOAN_NAME = /\b(student|school|college|tuition|navient|nelnet|sallie|fedloan|mohela)\b/i;

/** The type's icon, or for a loan, the kind of loan its name says it is. */
export function defaultIconForType(type, name = '') {
	if(type === 'loan' && name) {
		if(CAR_LOAN_NAME.test(name)) {
			return 'directions_car';
		}
		if(STUDENT_LOAN_NAME.test(name)) {
			return 'school';
		}
	}
	return DEFAULT_ICON_BY_TYPE[type] || 'account_balance';
}

/**
 * The logo an account is drawn with when it has no icon of its own: its institution's, if that has
 * one. An icon someone chose wins over it, and with neither the type's icon is drawn.
 *
 * `institution` is the group the account sits in, when the caller holds it (a PlaidAccount); the
 * account's own `institution` is drawn from that same group by the server, so either answers.
 */
export function logoForAccount(account, institution = null) {
	if(!account || account.icon) {
		return null;
	}
	return institution?.institution?.image_url || account.institution?.image_url || null;
}

/** The icon the account chose, or the default for its type and name. */
export function iconForAccount(account) {
	if(account && account.icon) {
		return account.icon;
	}
	const name = account ? [account.display_name, account.official_name, account.name].filter(Boolean).join(' ') : '';
	return defaultIconForType(account && account.type, name);
}

/**
 * A stable colour per name. The same institution has to look the same every render, so this hashes
 * the name rather than picking at random, and stays in a mid lightness band so white reads on it.
 * Written #rrggbb, so a colour input can start from it.
 */
export function colorFromName(name) {
	name = name || '';
	let hash = 0;
	for(let index = 0; index < name.length; index++) {
		hash = (hash * 31 + name.charCodeAt(index)) % 360;
	}
	return hslToHex(hash, 0.45, 0.38);
}

function hslToHex(hue, saturation, lightness) {
	const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
	const channel = (offset) => {
		const k = (offset + hue / 30) % 12;
		const value = lightness - chroma / 2 * Math.max(-1, Math.min(k - 3, 9 - k, 1));
		return Math.round(value * 255).toString(16).padStart(2, '0');
	};
	return `#${channel(0)}${channel(8)}${channel(4)}`;
}

/** #rrggbb, what the server accepts. */
export function isHexColor(value) {
	return /^#[0-9a-fA-F]{6}$/.test(value || '');
}
